// src/pages/profile/Notifications.tsx
import React, { useState, useEffect } from 'react';
import {
  Bell, Check, CheckCheck, Trash2,
} from 'lucide-react';
import { Button, Card, Breadcrumb, Select } from '../../components/ui';
import { useNotificationStore } from '../../store/notificationStore';
import { formatDistanceToNow } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';

const NotificationsPage: React.FC = () => {
  const {
    notifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    fetchNotifications
  } = useNotificationStore();
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const unreadCount = notifications.filter(n => !n.read_at).length;

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'unread') return !n.read_at;
    if (filter === 'read') return !!n.read_at;
    return true;
  });

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[{ label: 'Notifikasi' }]} />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight">Notifikasi</h1>
          <p className="text-sm text-neutral-text-secondary mt-1">
            {unreadCount > 0 ? `${unreadCount} notifikasi belum dibaca` : 'Semua notifikasi sudah dibaca'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            options={[
              { value: 'all', label: 'Semua' },
              { value: 'unread', label: 'Belum Dibaca' },
              { value: 'read', label: 'Sudah Dibaca' },
            ]}
            value={filter}
            onChange={setFilter}
          />
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" onClick={() => markAllAsRead()} icon={<CheckCheck className="w-4 h-4" />}>
              Tandai Semua Dibaca
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {filteredNotifications.map((notif) => (
          <Card
            key={notif.id}
            hover
            padding="sm"
            className={`transition-all ${!notif.read_at ? 'bg-blue-50/50 border-l-4 border-l-teal-main' : ''}`}
          >
            <div className="flex items-start gap-4">
              <div className="text-2xl shrink-0 mt-0.5">{notif.data.icon || '🔔'}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className={`text-sm ${!notif.read_at ? 'font-bold' : 'font-medium'} text-neutral-text-primary`}>
                      {notif.data.title}
                    </h3>
                    <p className="text-sm text-neutral-text-secondary mt-0.5 line-clamp-2">
                      {notif.data.message}
                    </p>
                    <p className="text-xs text-neutral-text-secondary mt-1.5">
                      {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true, locale: idLocale })}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!notif.read_at && (
                      <button
                        onClick={() => markAsRead(notif.id)}
                        className="p-1.5 rounded-lg hover:bg-neutral-bg text-neutral-text-secondary"
                        title="Tandai dibaca"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => deleteNotification(notif.id)}
                      className="p-1.5 rounded-lg hover:bg-danger/10 text-neutral-text-secondary hover:text-danger"
                      title="Hapus"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        ))}

        {filteredNotifications.length === 0 && (
          <Card className="text-center py-12">
            <Bell className="w-12 h-12 mx-auto mb-3 text-neutral-border" />
            <p className="text-lg font-bold text-neutral-text-primary">Tidak ada notifikasi</p>
            <p className="text-sm text-neutral-text-secondary mt-1">
              {filter === 'unread' ? 'Semua notifikasi sudah dibaca' : 'Belum ada notifikasi'}
            </p>
          </Card>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;